import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { allToolCards } from '../data/toolsData';
import ToolIcon from '../components/tool-pages/ToolIcon';
import { useInView } from '../hooks/useInView';

const APP_URL = 'https://console.filemingle.com';

function ToolCard({ tool, index, inView }) {
  return (
    <Link
      to={`/${tool.slug}`}
      className="group relative rounded-2xl border border-slate-100 bg-white p-7 shadow-sm card-hover block overflow-hidden"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.5s ease ${index * 0.08}s, transform 0.5s ease ${index * 0.08}s`,
      }}
    >
      {/* Gradient accent top-right */}
      <div className={`absolute -top-8 -right-8 w-28 h-28 rounded-full bg-gradient-to-br ${tool.color} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-300`} />

      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center mb-5 shadow-md`}>
        <ToolIcon name={tool.icon} size={22} className="text-white" />
      </div>

      <h2 className="font-bold text-slate-900 text-lg mb-2 group-hover:text-brand-blue transition-colors duration-200">
        {tool.label}
      </h2>
      <p className="text-sm text-slate-500 leading-relaxed mb-5">{tool.description}</p>

      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
        Learn more
        <ArrowRight
          size={14}
          aria-hidden="true"
          className="group-hover:translate-x-1 transition-transform duration-200"
        />
      </span>
    </Link>
  );
}

export default function ToolsPage() {
  const [ref, inView] = useInView(0.05);

  return (
    <>
      <Helmet>
        <title>All FileMingle Tools — Free Online File Utilities</title>
        <meta
          name="description"
          content="Browse all FileMingle tools: PDF form builder, fillable PDF creator, image to PNG converter, JPG to PNG, image compressor, PNG compressor, and more."
        />
        <link rel="canonical" href="https://filemingle.com/tools" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-hero-gradient">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-32 w-[440px] h-[440px] rounded-full bg-brand-blue opacity-20 blur-3xl animate-float" />
          <div className="absolute -bottom-20 -right-20 w-[380px] h-[380px] rounded-full bg-brand-indigo opacity-25 blur-3xl animate-float-slow" />
        </div>
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-white/80 text-xs font-semibold tracking-wider uppercase mb-6">
            All Tools
          </span>
          <h1 className="text-5xl sm:text-6xl font-extrabold text-white leading-tight tracking-tight mb-6">
            All <span className="text-gradient-light">FileMingle</span> Tools
          </h1>
          <p className="text-lg text-white/65 max-w-xl mx-auto leading-relaxed">
            Free, browser-based file utilities — no software, no sign-up required.
          </p>
        </div>
      </section>

      {/* Tool grid */}
      <section className="py-20 sm:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div
            ref={ref}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {allToolCards.map((tool, i) => (
              <ToolCard key={tool.slug} tool={tool} index={i} inView={inView} />
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-4">
            Ready to use FileMingle?
          </h2>
          <p className="text-slate-500 mb-8 leading-relaxed">
            Launch the app and access all tools in one place — directly in your browser.
          </p>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-brand-gradient text-white font-bold text-base shadow-lg shadow-brand-blue/30 hover:shadow-brand-blue/50 hover:scale-105 transition-all duration-200"
          >
            Launch FileMingle App
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        </div>
      </section>
    </>
  );
}
